/**
 * New note — `docs/activity.md` §9.7, Board 11 G (620).
 *
 * **Attached to** (at least one) · *Note* with **Dictate**, the camera and
 * the paperclip · *Who can see it* **Office · Office and crew** · *On the
 * record* **Pin to the top** (pins it on the first link). Written through the
 * record composer's endpoint on the first link, the others as links.
 *
 * Opened from a thread's *Make → Log a call* it is the same sheet writing a
 * `call` (§11.5's words for what came of it live on the stop page's call log).
 */
import { useState } from 'react'
import { CALL_OUTCOMES, showsOnWords } from '@rowted/shared'
import { Dictation } from '../../components/dictation.js'
import { cn } from '../../lib/cn.js'
import { activityRequest } from '../api.js'
import { AttachedTo, cameWithWords, type AttachChip } from './attach.js'
import { makeActivity, openedFromWords, problemOf, useContact, usePrefillChips, useSetup, type NewPrefill } from './common.js'
import { FileChips, FileTools, useFiles } from './files.js'
import { Field, Gap, NewShell, Pair, Segments, SmallSwitch, WordsBox } from './kit.js'

export function NoteSheet({
  prefill, phone, onClose, onMade,
}: { prefill: NewPrefill; phone: boolean; onClose: () => void; onMade: () => void }) {
  const setup = useSetup()
  const [chips, setChips] = usePrefillChips(prefill)
  const [picked, setPicked] = useState<{ chip: AttachChip; brought: AttachChip[] } | null>(null)
  const [body, setBody] = useState('')
  const [visibility, setVisibility] = useState<'internal' | 'crew'>('internal')
  const [pin, setPin] = useState(false)
  const [outcome, setOutcome] = useState<string | null>(null)
  const files = useFiles()
  const [busy, setBusy] = useState(false)
  const [problem, setProblem] = useState<string | null>(null)
  const threadContact = useContact(prefill.fromConversationId ? prefill.contactId ?? null : null)
  const call = prefill.logCall === true

  const hint = cameWithWords(picked) ?? openedFromWords({ name: threadContact?.name ?? null, kind: 'thread', required: true, phone })
  const dictate = (said: string) => setBody((was) => (was ? `${was} ${said}` : said))

  const save = async () => {
    if (chips.length === 0) { setProblem('Attach it to a contact, property or job.'); return }
    if (!body.trim() && files.files.length === 0 && !outcome) { setProblem('Write the note.'); return }
    setBusy(true)
    setProblem(null)
    try {
      const made = await makeActivity({
        kind: call ? 'call' : 'note',
        body: body.trim() || undefined,
        visibility,
        outcome: call ? outcome : undefined,
        fileIds: files.files.map((file) => file.id),
        madeFromCommunicationId: undefined,
      }, chips)
      if (pin && chips[0]) {
        await activityRequest(`/activities/${made.id}/pin`, {
          method: 'POST', body: { record: { type: chips[0].type, id: chips[0].id }, pinned: true },
        })
      }
      onMade()
      onClose()
    } catch (cause) {
      setProblem(problemOf(cause))
    } finally {
      setBusy(false)
    }
  }

  return (
    <NewShell title={call ? 'Log a call' : 'New note'} sub={setup?.sub ?? ''} look="note" width={620} phone={phone}
      note={showsOnWords(chips.map((chip) => ({ type: chip.type, label: chip.label })))}
      primary={call ? 'Save call' : 'Save note'} phoneAction="Create" onPrimary={() => void save()} busy={busy || files.busy}
      dirty={body.trim().length > 0 || files.files.length > 0} onClose={onClose} problem={problem ?? files.problem}>
      <AttachedTo chips={chips} required phone={phone} hint={hint} id="new-note-attached"
        onChange={(next, pick) => { setChips(next); setPicked(pick ?? null) }} />
      <Gap />
      {call && (
        <>
          <Field label="How it went" phone={phone}>
            <div className="flex flex-wrap gap-1.5">
              {CALL_OUTCOMES.map((one) => (
                <button key={one} type="button" aria-pressed={outcome === one} onClick={() => setOutcome(one)}
                  className={cn('inline-flex h-[30px] items-center rounded-chip border px-2.5 text-[12px] font-semibold',
                    outcome === one ? 'border-accent-line bg-accent-soft text-accent-dark' : 'border-line bg-surface text-text')}>
                  {one}
                </button>
              ))}
            </div>
          </Field>
          <Gap />
        </>
      )}
      <Field label={call ? 'What was said' : 'Note'} phone={phone}>
        <WordsBox label={call ? 'What was said' : 'Note'} value={body} onChange={setBody} minHeight={96} phone={phone}
          placeholder={phone ? 'Add a note' : undefined}
          phoneTool={<span className="text-accent"><Dictation variant="icon" onText={dictate} /></span>}
          tools={(
            <>
              <Dictation variant="chip" onText={dictate} />
              <FileTools onFiles={(list) => void files.add(list)} />
              <span className="flex-1" />
            </>
          )} />
        <FileChips files={files.files} onRemove={files.remove} />
      </Field>
      <Gap />
      <Pair phone={phone}>
        <Field label="Who can see it" phone={phone}>
          <Segments label="Who can see it" value={visibility} onChange={setVisibility} height={phone ? 36 : 28} fill
            options={[{ value: 'internal', label: 'Office' }, { value: 'crew', label: 'Office and crew' }]} />
        </Field>
        <Field label="On the record" phone={phone}>
          <button type="button" role="switch" aria-checked={pin} onClick={() => setPin((was) => !was)}
            className="flex h-9 items-center gap-2 text-[13px]">
            <SmallSwitch on={pin} />Pin to the top
          </button>
        </Field>
      </Pair>
    </NewShell>
  )
}
